import React, { useEffect, useRef, useState } from 'react';
import {
    Button,
    Spinner,
    Alert,
    Container,
    Card,
    CardBody,
    Badge,
    Modal,
    ModalBody,
    Table,
    Input,
    InputGroup,
    InputGroupText,
    FormGroup,
    Label,
    Row,
    Col,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from 'reactstrap';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import {
    getAllProjects,
    getTeamLeadProjects,
    getTeammember,
    getTLProjectDetails,
} from '../../Api/ApiUrlConst';
import { getMyProjects } from "../../Api/ApiUrlConst";
import { getProjectDetails } from "../../Api/ApiUrlConst";
import { useAuth } from '../../Context/AuthContext';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import { Edit as EditIcon } from 'lucide-react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

// --- Interfaces ---
interface ProjectDTO {
    id: number;
    projectID: string;
    projectName: string;
    createdByName: string;
    startDate: string;
    endDate: string;
    taskCount: number;
    tasks?: TaskDTO[];
}
interface TaskDTO {
    id: number;
    description: string;
    projectId: number;
    projectName: string;
    assignedToId: string;
    assignedToName: string;
    createdById: string;
    createdByName: string;
    dueDate: string;
    completionDate: string;
    status: string;
    priority: string;
    percentCompleted: number;
    remarks: string;
    issues: string;
    subTasks: SubTaskDTO[];
}
interface SubTaskDTO {
    id: number;
    taskId: number;
    parentSubTaskId: number | null;
    description: string;
    assignedToId: string;
    status: string;
    priority: string;
    dueDate: string;
    percentCompleted: number;
    workHours: number;
    completionDate: string | null;
    remarks: string;
    issues: string;
}
interface TeamMemberDTO {
    userID: string;
    name: string;
    department: string;
}

// --- Style Maps ---
const statusColorMap: Record<string, string> = {
    'Yet to Start': 'status-yet-to-start',
    YetToStart: 'status-yet-to-start',

    'On Hold': 'status-on-hold',
    OnHold: 'status-on-hold',

    'Waiting for Input': 'status-waiting-input',
    WaitingForInput: 'status-waiting-input',

    'Modelling in Progress': 'status-modelling-progress',
    ModellingInProgress: 'status-modelling-progress',

    'Modelling ToDo': 'status-modelling-Todo',
    ModellingTodo: 'status-modelling-Todo',

    'Modelling Completed': 'status-modelling-completed',
    ModellingCompleted: 'status-modelling-completed',

    'Drafting in Progress': 'status-drafting-progress',
    DraftingInProgress: 'status-drafting-progress',

    'Drafting ToDo': 'status-drafting-Todo',
    DraftingTodo: 'status-drafting-Todo',

    'Drafting Completed': 'status-drafting-completed',
    DraftingCompleted: 'status-drafting-completed',

    'QC ToDo': 'status-qc-Todo',
    QCTodo: 'status-qc-Todo',

    'QC in Progress': 'status-qc-progress',
    QCInProgress: 'status-qc-progress',

    'QC Completed': 'status-qc-completed',
    QCCompleted: 'status-qc-completed',

    'Client Review in Progress': 'status-client-review-progress',
    ClientReviewInProgress: 'status-client-review-progress',

    'Client Review ToDo': 'status-client-review-Todo',
    ClientReviewTodo: 'status-client-review-Todo',



    'Client Review Completed': 'status-client-review-completed',
    ClientReviewCompleted: 'status-client-review-completed',

    'Revision in Progress': 'status-Revision-progress',
    RevisionInProgress: 'status-Revision-progress',

    'Revision ToDo': 'status-Revision-Todo',
    RevisionTodo: 'status-Revision-Todo',

    'Revision Completed': 'status-Revision-completed',
    RevisionCompleted: 'status-Revision-completed',


    'Final Correction in Progress': 'status-final-correction-progress',
    FinalCorrectionInProgress: 'status-final-correction-progress',

    'Final Correction ToDo': 'status-final-correction-Todo',
    FinalCorrectionTodo: 'status-final-correction-Todo',

    'Final Correction Completed': 'status-final-correction-completed',
    FinalCorrectionCompleted: 'status-final-correction-completed',

    'Not In Scope': 'status-Not-In-Scope',
    NotInScope: 'status-Not-In-Scope',

    Released: 'status-released',
    'Completed & Closed': 'status-completed',
    Completed: 'status-completed',

    Cancelled: 'status-cancelled',
    Issues: 'status-issues',

 CompletedAndReleased: "status-completedandreleased",


  NotApplicable1: "status-Drafting-Released",
};

const priorityColorMap: Record<string, string> = {
    Low: 'priority-low',
    Medium: 'priority-medium',
    High: 'priority-high',
    Critical: 'priority-critical',
};

const TeamLeadProjectsWithTasks: React.FC = () => {
    const { role, userID, department } = useAuth();
    const [projects, setProjects] = useState<ProjectDTO[]>([]);
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [projectError, setProjectError] = useState<string | null>(null);
    const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
    const [loadingTasks, setLoadingTasks] = useState(false);
    const [tasksError, setTasksError] = useState<string | null>(null);
    const [teamMembers, setTeamMembers] = useState<TeamMemberDTO[]>([]);
    const [showSubtasksForTask, setShowSubtasksForTask] = useState<Record<number, boolean>>({});
    const [inlineEditTaskId, setInlineEditTaskId] = useState<number | null>(null);
    const [inlineEditSubTaskId, setInlineEditSubTaskId] = useState<number | null>(null);
    const [inlineNewTaskProjectId, setInlineNewTaskProjectId] = useState<number | null>(null);
    const [inlineNewSubtaskTaskId, setInlineNewSubtaskTaskId] = useState<Record<number, boolean>>({});
    const [remarksModalOpen, setRemarksModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRemarks, setSelectedRemarks] = useState('');
    const [showInlineCreateTask, setShowInlineCreateTask] = useState(false);
    const [showAllTasks, setShowAllTasks] = useState(true);
    const [formData, setFormData] = useState({ status: "" });

    const [columnsOrder, setColumnsOrder] = React.useState([
        { key: "number", label: "#", width: "4%" },
        { key: "description", label: "Description", width: "26%" },
        { key: "assigned", label: "Assigned To", width: "10%" },
        { key: "status", label: "Status", width: "13%" },
        { key: "priority", label: "Priority", width: "8%" },
        { key: "dueDate", label: "Due Date", width: "9%" },
        { key: "completion", label: "Completion", width: "9%" },
        { key: "percent", label: "%", width: "5%" },
        { key: "remarks", label: "Remarks", width: "11%" },
        { key: "issues", label: "Issues", width: "11%" }
    ]);

    const formatStatusLabel = (status: string) => {
        const overrides: Record<string, string> = {
            Completed: "Ready for Release",
            YetToStart: "Yet to Start",
      NotApplicable:"Not In Scope"

        };

        if (overrides[status]) {
            return overrides[status];
        }

        // generic formatter for the rest
        return status
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
            .replace(/And/g, "&")
            .trim();
    };

    const dragColIndex = useRef<number | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const toggle = () => setDropdownOpen((prev) => !prev);

    useEffect(() => {
        if (role !== 'TeamLeader' && role !== 'ProjectManager') return;
        (async () => {
            await fetchProjects();
            await fetchTeamMembers();
        })();
    }, [role]);

    async function fetchTeamMembers() {
        try {
            const res = await getTeammember();
            const data = res.data?.$values || res.data || [];
            setTeamMembers(data);
        } catch { }
    }
    async function fetchProjects() {
        setLoadingProjects(true);
        setProjectError(null);

        try {
            let res;

            if (role === "TeamLeader") {
                res = await getTeamLeadProjects();
            } else if (role === "ProjectManager") {
                res = await getMyProjects();
            } else if (role === "Admin") {
                res = await getAllProjects();   // 👈 Admin access added
            } else {
                return [];
            }

            const data = res.data?.$values || res.data || [];
            const projectsList = data.map((p: any) => ({ ...p, id: Number(p.id) }));
            setProjects(projectsList);

            return projectsList;
        }
        catch (error: any) {
            const message =
                error.response?.data?.message || "Failed to load projects";
            setProjectError(message);
            toast.error(message);
            return [];
        } finally {
            setLoadingProjects(false);
        }
    }

    async function fetchProjectTasks(projectId: number) {
        setLoadingTasks(true);
        setTasksError(null);

        try {
            const project = projects.find(p => p.id === projectId);
            if (!project) throw new Error("Project not found");

            let res;
            if (role === "TeamLeader") {
                res = await getTLProjectDetails(project.projectID);
            } else if (role === "ProjectManager") {
                res = await getProjectDetails(project.projectID);
            } else {
                throw new Error("Unauthorized role");
            }

            if (!res.data) throw new Error("No data received");

            const tasksArray = Array.isArray(res.data.tasks)
                ? res.data.tasks
                : res.data.tasks?.$values || [];

            const tasksData: TaskDTO[] = tasksArray.map((task: any) => ({
                ...task,
                description: task.description || task.title || "",
                issues: task.issues || "",
                subTasks: (
                    Array.isArray(task.subTasks)
                        ? task.subTasks
                        : task.subTasks?.$values || []
                ).map((sub: any) => ({
                    ...sub,
                    taskId: task.id,
                    parentSubTaskId: sub.parentSubTaskId || null,
                    description: sub.description || sub.title || "",
                    assignedToId: sub.assignedToId || sub.assigned || "",
                    priority: sub.priority || "Medium",
                    percentCompleted: sub.percentCompleted || 0,
                    issues: sub.issues || "",
                })),
            }));

            setProjects(prev =>
                prev.map(p => (p.id === projectId ? { ...p, tasks: tasksData } : p))
            );

            setShowSubtasksForTask({});
            setInlineNewTaskProjectId(projectId);
        } catch (error: any) {
            console.error("Error fetching project details:", error);
            const message =
                error.response?.data?.message ||
                "Error loading tasks for this project";
            setTasksError(message);
            toast.error(message);
        } finally {
            setLoadingTasks(false);
        }
    }
    function toggleSubtasksDropdown(taskId: number) {
        setShowSubtasksForTask(prev => {
            const isCurrentlyOpen = prev[taskId];
            if (isCurrentlyOpen) {
                return {};
            }
            return { [taskId]: true };
        });

        setInlineNewSubtaskTaskId(prevInline => ({
            ...prevInline,
            [taskId]: false,
        }));
    }
    async function refreshTasksList() {
        if (!selectedProjectId) return;
        await fetchProjectTasks(selectedProjectId);
    }
    function openInlineEditTask(taskId: number) {
        setInlineEditTaskId(taskId);
        setInlineEditSubTaskId(null);
        setInlineNewSubtaskTaskId({});
    }
    function closeInlineEditTask(refresh = false) {
        setInlineEditTaskId(null);
        if (refresh) {
            refreshTasksList();
        }
    }
    function openInlineEditSubTask(subTaskId: number, taskId: number) {
        setInlineEditSubTaskId(subTaskId);
        setInlineEditTaskId(null);
        setInlineNewSubtaskTaskId({});
        setShowSubtasksForTask(prev => ({ ...prev, [taskId]: true }));
    }
    async function closeInlineEditSubTask(refresh = false, taskId?: number) {
        setInlineEditSubTaskId(null);

        if (refresh && taskId) {
            await refreshTasksList();
            setShowSubtasksForTask({ [taskId]: true });
        }
    }
    function openInlineCreateSubTask(taskOrSubtaskId: number) {
        setInlineNewSubtaskTaskId(prev => ({ ...prev, [taskOrSubtaskId]: true }));
        setInlineEditTaskId(null);
        setInlineEditSubTaskId(null);
        setShowSubtasksForTask(prev => ({ ...prev, [taskOrSubtaskId]: true }));
    }
    async function closeInlineCreateSubTask(taskOrSubtaskId: number, refresh = false) {
        setInlineNewSubtaskTaskId(prev => ({ ...prev, [taskOrSubtaskId]: false }));
        if (refresh) {
            refreshTasksList();
            setInlineNewSubtaskTaskId(prev => ({ ...prev, [taskOrSubtaskId]: true }));
        }
    }
    const handleViewMore = (remarks: string) => {
        setSelectedRemarks(remarks);
        setRemarksModalOpen(true);
    };
    const openInlineCreateTask = () => setShowInlineCreateTask(true);
    const closeInlineCreateTask = () => setShowInlineCreateTask(false);

    const formatDate = (dateStr: string | null | undefined) => {
        if (!dateStr) return '—';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const handleProjectChange = async (event: React.ChangeEvent<any>) => {
        const selectedValue = event.target.value;

        setInlineEditTaskId(null);
        setInlineEditSubTaskId(null);

        if (selectedValue === 'select' || selectedValue === '') {
            setSelectedProjectId(null);
            setProjects(prev => prev.map(p => ({ ...p, tasks: [] })));
            setTasksError(null);
            return;
        }

        const projectId = parseInt(selectedValue, 10);
        if (!isNaN(projectId)) {
            setSelectedProjectId(projectId);
            await fetchProjectTasks(projectId);
        }
    };

    if (role !== 'TeamLeader' && role !== 'ProjectManager') {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <Alert color="danger">Only Team Leaders can access this page</Alert>
            </Container>
        );
    }

    const getDueDateColor = (dateString: string | null | undefined, status: string): string => {
        if (!dateString) return 'secondary';
        if (status === 'Completed' || status === 'Released') return 'success';

        const dueDate = new Date(dateString);
        const today = new Date();
        dueDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        const diffTime = dueDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return 'danger';
        }

        if (diffDays <= 2) {
            return 'warning';
        }

        return 'success';
    };
    const getDueDateMessage = (dateString: string | null | undefined, status: string): string | null => {
        if (!dateString || status === 'Completed' || status === 'Released') return null;
        const dueDate = new Date(dateString);
        const today = new Date();
        dueDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        const diffTime = dueDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return `This project is overdue by ${Math.abs(diffDays)} day(s).`;
        }
        if (diffDays === 0) {
            return "This project is due today!";
        }
        if (diffDays <= 2) {
            return `This project is due in ${diffDays} day(s).`;
        }

        return null;
    };
    const getProjectOverallStatus = (tasks: TaskDTO[]): string => {
        const finishedStatuses = ['Completed', 'Released'];

        if (!tasks || tasks.length === 0) {
            return 'InProgress';
        }

        const allItemsAreFinished = tasks.every(task => {
            const taskIsFinished = finishedStatuses.includes(task.status);
            const subtasksAreFinished = (task.subTasks ?? []).every(subtask =>
                finishedStatuses.includes(subtask.status)
            );
            return taskIsFinished && subtasksAreFinished;
        });

        return allItemsAreFinished ? 'Completed' : 'InProgress';
    };

    // === Filtering and task list preparation ===
    const selectedProject = projects.find(p => p.id === selectedProjectId);
    const allTasksForProject = selectedProject?.tasks || [];

    const tasksToShowBasedOnToggle = allTasksForProject;


    const filteredTasks = tasksToShowBasedOnToggle.filter(task => {
        if (!searchTerm.trim()) return true;
        const lower = searchTerm.toLowerCase();

        const assignedMember = teamMembers.find(m => m.userID === task.assignedToId);
        const assigneeName = assignedMember ? assignedMember.name.toLowerCase() : '';

        const descriptionMatch = task.description?.toLowerCase().includes(lower);
        const ownerMatch = task.createdByName?.toLowerCase().includes(lower);
        const statusMatch = task.status?.toLowerCase().includes(lower);
        const priorityMatch = task.priority?.toLowerCase().includes(lower);

        // ✅ Format Due Date as "dd MMM" (e.g. "04 Sep")
        const dueDateMatch = task.dueDate
            ? new Date(task.dueDate)
                .toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) // "04 Sep"
                .toLowerCase()
                .includes(lower)
            : false;

        // ✅ Format Completion Date as "dd MMM" (e.g. "27 Aug")
        const completionDateMatch = task.completionDate
            ? new Date(task.completionDate)
                .toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) // "27 Aug"
                .toLowerCase()
                .includes(lower)
            : false;

        return (
            assigneeName.includes(lower) ||
            descriptionMatch ||
            statusMatch ||
            priorityMatch ||
            ownerMatch ||
            completionDateMatch ||
            dueDateMatch
        );
    });

    const projectStatus = getProjectOverallStatus(allTasksForProject);

    const handleWarningIconClick = () => {
        if (!selectedProject) return;
        const message = getDueDateMessage(selectedProject.endDate, projectStatus);
        if (message) {
            toast.warning(message, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
            });
        }
    };

    const getWidth = (text: any) => {
        const baseWidth = 100;
        const incrementPerChar = 8;
        return baseWidth + text.length * incrementPerChar;
    };

    const handleDragStart = (e: React.DragEvent<HTMLTableHeaderCellElement>, index: number) => {
        dragColIndex.current = index;
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e: React.DragEvent<HTMLTableHeaderCellElement>) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent<HTMLTableHeaderCellElement>, dropIndex: number) => {
        e.preventDefault();
        if (dragColIndex.current === null) return;
        const tempCols = [...columnsOrder];
        const [moved] = tempCols.splice(dragColIndex.current, 1);
        tempCols.splice(dropIndex, 0, moved);
        setColumnsOrder(tempCols);
        dragColIndex.current = null;
    };


    // --- Render Sorted Tasks and Subtasks ---
    // Sorting code explained: Completed tasks/subtasks always last,
    // original order for others unchanged.

    return (
        <Container fluid>
            <Card className="shadow-sm border-0 rounded-3">
                <CardBody>
                    <Row className=" mb-1">
                        <Col sm={12} md={6} lg={4}>
                            <Dropdown className='drop-select shadow-sm rounded-3' isOpen={dropdownOpen} toggle={toggle} style={{ width: 'max-content' }} >
                                <DropdownToggle caret color="white" className="w-100 text-start fw-semibold border rounded-3 py-2 px-3 d-flex justify-content-between align-items-center">
                                    {selectedProject
                                        ? `${selectedProject.projectID} - ${selectedProject.projectName}`
                                        : 'Select a project'}
                                </DropdownToggle>

                                <DropdownMenu className="custom-dropdown-menu shadow rounded-3 p-2">
                                    {projects.map((p) => (
                                        <DropdownItem
                                            key={p.id}
                                            onClick={() =>
                                                handleProjectChange({
                                                    target: { value: String(p.id) }
                                                } as React.ChangeEvent<HTMLInputElement>)}
                                            className="custom-dropdown-item rounded-2 py-2"
                                        >
                                            <span className="custom-dropdown-item rounded-2 py-2">
                                                {p.projectID} - {p.projectName}
                                            </span>
                                        </DropdownItem>
                                    ))}
                                </DropdownMenu>
                            </Dropdown>
                        </Col>
                        {selectedProject && (
                            <Col lg={8} md={6} sm={12} className="project-details">
                                <div className="detail-row">
                                    <EventAvailableIcon color="secondary" style={{ marginRight: '5px' }} />
                                    <Badge color="success">{formatDate(selectedProject.startDate)}</Badge>
                                    <span className="date-separator mx-2">-</span>
                                    <Badge color={getDueDateColor(selectedProject.endDate, projectStatus)}>
                                        {formatDate(selectedProject.endDate)}
                                    </Badge>
                                </div>

                                <div className="detail-row">
                                    <AccountCircleIcon color="success" style={{ marginRight: '5px' }} />
                                    <span>{selectedProject.createdByName}</span>
                                </div>

                                {(getDueDateColor(selectedProject.endDate, projectStatus) === 'warning' || getDueDateColor(selectedProject.endDate, projectStatus) === 'danger') && (
                                    <WarningAmberIcon
                                        id="project-warning-icon"
                                        fontSize="large"
                                        className="blinking-warning mt-1"
                                        style={{
                                            marginLeft: '8px',
                                            color: '#ffbf00ff',
                                            fontWeight: 'bolder',
                                            cursor: 'pointer'
                                        }}
                                        onClick={handleWarningIconClick}
                                    />
                                )}
                                <div className='d-flex justify-content-end align-items-center'>
                                    <InputGroup className="me-3">
                                        <InputGroupText>
                                            <PersonSearchIcon />
                                        </InputGroupText>
                                        <Input
                                            placeholder="Search Tasks"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{ width: "300px" }}
                                        />
                                    </InputGroup>
                                </div>
                                <div className='d-flex align-items-center'>
                                    <FormGroup switch className="mb-0">
                                        <Input
                                            type="switch"
                                            id="showAllTasksSwitch"
                                            checked={showAllTasks}
                                            onChange={() => setShowAllTasks(!showAllTasks)}
                                            style={{
                                                display: 'none'
                                            }}
                                        />
                                        <Label for="showAllTasksSwitch" className="form-check-label" style={{ userSelect: 'none', display: 'none' }}>
                                            Show All Tasks
                                        </Label>
                                    </FormGroup>
                                </div>
                            </Col>
                        )}
                    </Row>

                    {selectedProjectId === null && <Alert color="info" className="text-center">Please select a project to view its tasks.</Alert>}
                    {loadingTasks && <div className="text-center p-4"><Spinner /> <p>Loading tasks...</p></div>}
                    {tasksError && <Alert color="danger">{tasksError}</Alert>}
                    {!loadingTasks && selectedProject && filteredTasks.length === 0 && (
                        <Alert color="info" className="text-center">
                            {searchTerm ? `No tasks found for "${searchTerm}".` : "No tasks found for this project."}
                        </Alert>
                    )}

                    {!loadingTasks && selectedProject && (
                        <div className="task-table-wrapper">
                            <Table responsive size="sm" style={{ tableLayout: 'fixed', minWidth: "600px" }}  >
                                <thead style={{ backgroundImage: 'linear-gradient(to right, #004080, #0059b3)', color: 'white' }}>
                                    <tr>
                                        {columnsOrder.map((col, index) => (
                                            <th
                                                key={col.key}
                                                style={{ width: col.width, cursor: "move" }}
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, index)}
                                                onDragOver={handleDragOver}
                                                onDrop={(e) => handleDrop(e, index)}
                                            >
                                                {col.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...filteredTasks]
                                        .sort((a, b) => {
                                            if (a.status === 'Completed' && b.status !== 'Completed') return 1;
                                            if (b.status === 'Completed' && a.status !== 'Completed') return -1;
                                            return 0;
                                        })
                                        .map((task) => (
                                            <React.Fragment key={task.id}>

                                                {/* Inline Edit Task Row */}

                                                {/* ==== Main Task Row ==== */}
                                                <tr style={{ backgroundColor: '#d8d8dbff' }}>
                                                    {columnsOrder.map((col) => {
                                                        switch (col.key) {
                                                            case "number":
                                                                return (
                                                                    <td key={col.key} className="align-middle text-center" >
                                                                        {task.subTasks && (
                                                                            <Button
                                                                                color="link"
                                                                                size="sm"
                                                                                onClick={() => toggleSubtasksDropdown(task.id)}
                                                                                style={{ padding: 0 }}
                                                                            >
                                                                                {task.subTasks && task.subTasks.length > 0 && (
                                                                                    showSubtasksForTask[task.id] ? (
                                                                                        <ExpandLessIcon />
                                                                                    ) : (
                                                                                        <ExpandMoreIcon />
                                                                                    )
                                                                                )}

                                                                            </Button>
                                                                        )}
                                                                    </td>
                                                                );
                                                            case "description":
                                                                return (
                                                                    <td key={col.key} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }} data-label="Description">
                                                                        {task.description}
                                                                    </td>
                                                                );
                                                            case "assigned":
                                                                return (
                                                                    <td key={col.key} data-label="Assigned">
                                                                        {teamMembers.find(m => m.userID === task.assignedToId)?.name || '—'}
                                                                    </td>
                                                                );

                                                            case "status":
                                                                return (
                                                                    <td key={col.key} data-label="Status">
                                                                        <Badge className={statusColorMap[task.status] || 'status-pending'}>
                                                                            {formatStatusLabel(task.status)}
                                                                        </Badge>
                                                                    </td>
                                                                );
                                                            case "priority":
                                                                return (
                                                                    <td key={col.key} data-label="Priority">
                                                                        <Badge className={priorityColorMap[task.priority] || 'priority-medium'}>
                                                                            {task.priority}
                                                                        </Badge>
                                                                    </td>
                                                                );
                                                            case "dueDate":
                                                                return <td key={col.key} data-label="Due Date">{formatDate(task.dueDate)}</td>;
                                                            case "completion":
                                                                return <td key={col.key} data-label="Completion Date">{formatDate(task.completionDate)}</td>;
                                                            case "percent":
                                                                return <td key={col.key} data-label="Percent">{task.percentCompleted}%</td>;
                                                            case "actions":
                                                                return (
                                                                    <td key={col.key} className="text-center">
                                                                        {(() => {
                                                                            // find task assignee’s department
                                                                            const assignee = teamMembers.find(m => m.userID === task.assignedToId);
                                                                            const canEdit = assignee && assignee.department === department;

                                                                            return canEdit ? (
                                                                                <Button
                                                                                    size="sm"
                                                                                    onClick={() => openInlineEditTask(task.id)}
                                                                                    className="border-0 rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                                                                                    style={{
                                                                                        width: "32px",
                                                                                        height: "32px",
                                                                                        backgroundColor: "#e7f3ff",
                                                                                        transition: "all 0.2s ease-in-out",
                                                                                    }}
                                                                                    onMouseEnter={(e) => {
                                                                                        e.currentTarget.style.backgroundColor = "#d0e7ff";
                                                                                        e.currentTarget.style.transform = "scale(1.05)";
                                                                                    }}
                                                                                    onMouseLeave={(e) => {
                                                                                        e.currentTarget.style.backgroundColor = "#e7f3ff";
                                                                                        e.currentTarget.style.transform = "scale(1)";
                                                                                    }}
                                                                                >
                                                                                    <EditIcon fontSize="small" className="text-primary" />
                                                                                </Button>
                                                                            ) : null;
                                                                        })()}
                                                                    </td>

                                                                );
                                                            default:
                                                                return null;
                                                        }
                                                    })}
                                                </tr>


                                                {/* ==== Subtasks ==== */}
                                                {showSubtasksForTask[task.id] &&
                                                    [...(task.subTasks ?? [])]
                                                        .sort((a, b) => {
                                                            if (a.status === 'Completed' && b.status !== 'Completed') return 1;
                                                            if (b.status === 'Completed' && a.status !== 'Completed') return -1;
                                                            return 0;
                                                        })
                                                        .map((subtask) => (
                                                            <React.Fragment key={subtask.id}>

                                                                <tr style={{ backgroundColor: '#fffbe6', borderTop: '1px solid #dee2e6' }}>
                                                                    {columnsOrder.map((col) => {
                                                                        switch (col.key) {
                                                                            case "number":
                                                                                return <td key={col.key}></td>;
                                                                            case "description":
                                                                                return (
                                                                                    <td data-label="description"
                                                                                        key={col.key}
                                                                                        style={{
                                                                                            paddingLeft: '2rem',
                                                                                            whiteSpace: 'normal',
                                                                                            wordBreak: 'break-word',
                                                                                        }}
                                                                                    >
                                                                                        {subtask.description}
                                                                                    </td>
                                                                                );
                                                                            case "assigned":
                                                                                return (
                                                                                    <td key={col.key} data-label="assigned">
                                                                                        {teamMembers.find(m => m.userID === subtask.assignedToId)?.name || '—'}
                                                                                    </td>
                                                                                );
                                                                            case "createdByName":
                                                                                return (
                                                                                    <td key={col.key}>
                                                                                        {task.createdByName}
                                                                                    </td>
                                                                                );
                                                                            case "status":
                                                                                return (
                                                                                    <td key={col.key}>
                                                                                        <Badge className={statusColorMap[subtask.status] || 'status-pending'}>
                                                                                            {formatStatusLabel(subtask.status)}
                                                                                        </Badge>
                                                                                    </td>
                                                                                );
                                                                            case "priority":
                                                                                return (
                                                                                    <td key={col.key}>
                                                                                        <Badge className={priorityColorMap[subtask.priority] || 'priority-medium'}>
                                                                                            {subtask.priority}
                                                                                        </Badge>
                                                                                    </td>
                                                                                );
                                                                            case "dueDate":
                                                                                return <td key={col.key}>{formatDate(subtask.dueDate)}</td>;
                                                                            case "completion":
                                                                                return <td key={col.key}>{formatDate(subtask.completionDate)}</td>;
                                                                            case "percent":
                                                                                return <td key={col.key}>{subtask.percentCompleted}%</td>;
                                                                            case "actions":
                                                                                return (
                                                                                    <td key={col.key} className="text-center">
                                                                                        {(() => {
                                                                                            // find subtask assignee’s department
                                                                                            const assignee = teamMembers.find(m => m.userID === subtask.assignedToId);
                                                                                            const canEdit = assignee && assignee.department === department;

                                                                                            return canEdit ? (
                                                                                                <Button
                                                                                                    size="sm"
                                                                                                    onClick={() => openInlineEditSubTask(subtask.id, task.id)}
                                                                                                    className="border-0 rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                                                                                                    style={{
                                                                                                        width: "32px",
                                                                                                        height: "32px",
                                                                                                        backgroundColor: "#e7f3ff",
                                                                                                        transition: "all 0.2s ease-in-out",
                                                                                                    }}
                                                                                                    onMouseEnter={(e) => {
                                                                                                        e.currentTarget.style.backgroundColor = "#d0e7ff";
                                                                                                        e.currentTarget.style.transform = "scale(1.05)";
                                                                                                    }}
                                                                                                    onMouseLeave={(e) => {
                                                                                                        e.currentTarget.style.backgroundColor = "#e7f3ff";
                                                                                                        e.currentTarget.style.transform = "scale(1)";
                                                                                                    }}
                                                                                                >
                                                                                                    <EditIcon fontSize="small" className="text-primary" />
                                                                                                </Button>
                                                                                            ) : null;
                                                                                        })()}
                                                                                    </td>


                                                                                );
                                                                            default:
                                                                                return null;
                                                                        }
                                                                    })}
                                                                </tr>

                                                            </React.Fragment>
                                                        ))}

                                            </React.Fragment>
                                        ))}

                                </tbody>
                            </Table>
                        </div>
                    )}

                    <Modal isOpen={remarksModalOpen} toggle={() => setRemarksModalOpen(false)} centered>
                        <ModalBody>
                            <textarea
                                className="form-control border rounded bg-light text-dark"
                                readOnly
                                style={{ whiteSpace: 'pre-wrap', height: '300px', resize: 'vertical' }}
                                value={selectedRemarks || ""}
                            />
                        </ModalBody>
                    </Modal>
                </CardBody>
            </Card>
        </Container>
    );
};

export default TeamLeadProjectsWithTasks;
